/**
 * A FELÜLETEN KIVÁLASZTOTT CÉG (#73) — egy helyen, a böngészőben.
 *
 * A választás GLOBÁLIS: mindhárom felületre érvényes, és oldalváltás meg
 * újratöltés után is megmarad. Az `api()` minden kéréshez hozzáteszi
 * (`X-Company-Id`), a szerver pedig a felhasználó hatókörével METSZI —
 * a választás szűkít, nem tágít (`company-scope.ts`, `narrowToSelectedCompany`).
 *
 * ⚠️ A BÖNGÉSZŐ TÁROLÓJA KÉNYELEM, NEM JOGOSULTSÁG. Ha a tároló üres vagy
 * elérhetetlen (privát ablak, tiltott süti), a választás „az összes cég" —
 * ugyanaz, mint fejléc nélkül. Ha a tárolt cég közben kiesett a hatókörből,
 * a szerver 403-mal válaszol, és a választás itt törlődik (`api-client.ts`).
 */

import { useSyncExternalStore } from 'react';

const SELECTED_KEY = 'fakt.company';
const RECENT_KEY = 'fakt.company.recent';

/** Ennyi cég áll elöl az üres keresésnél. */
export const RECENT_LIMIT = 5;

const listeners = new Set<() => void>();

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // A tároló nélkül a választás a lap élettartamára szól — a memóriában.
  }
}

/** A tároló nélküli ág tartaléka: a választás legalább a lapon belül él. */
let memory: string | null = read(SELECTED_KEY);

/** A kiválasztott cég azonosítója, vagy `null` = az összes (látható) cég. */
export function getSelectedCompany(): string | null {
  return memory;
}

export function setSelectedCompany(companyId: string | null): void {
  if (companyId === memory) {
    return;
  }
  memory = companyId;
  write(SELECTED_KEY, companyId);
  if (companyId !== null) {
    rememberRecent(companyId);
  }
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A kiválasztott cég, Reactből — változáskor újrarajzol. */
export function useSelectedCompany(): string | null {
  return useSyncExternalStore(subscribe, getSelectedCompany);
}

/**
 * KIJELENTKEZÉSKOR mindkettő törlődik (#76): a választás és a „legutóbbi"
 * lista is a munkamenet része. Egy közös gépen a következő belépő különben
 * az előző felhasználó cégére szűkítve indulna.
 */
export function forgetCompanySelection(): void {
  write(RECENT_KEY, null);
  setSelectedCompany(null);
}

/** A legutóbb választott cégek, a legfrissebb elöl. */
export function recentCompanies(): string[] {
  const raw = read(RECENT_KEY);
  if (raw === null) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function rememberRecent(companyId: string): void {
  const next = [companyId, ...recentCompanies().filter((id) => id !== companyId)].slice(
    0,
    RECENT_LIMIT
  );
  write(RECENT_KEY, JSON.stringify(next));
}
